"use client";

import { gql, useQuery } from "@apollo/client";
import {
  AccessTime,
  Assignment,
  CheckCircle,
  DirectionsCar,
  History,
  LocationOn,
  Person,
  Refresh,
  Speed
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Typography
} from "@mui/material";
import { DateTime } from "luxon";
import React, { useState } from "react";
import { useSelector } from "react-redux";

import { IUserJob } from "../../../reducers/jobSchedular/types";
import { PartnerSkills } from "../Settings/Pricing/AllocationRules/utils";
import { BarChartComponent } from "./../../Shared/BarChartComponent";
import { GET_DASHBOARD_METRICS } from "./../../Shared/getDashboardMetrics";
import { HorizontalBarChart } from "./../../Shared/HorizontalBarChart";
import JobLocationDistributionChart from "./../../Shared/JobLocationDistributionChart";
import { KpiCard } from "./../../Shared/KpiCard";
import { PieChartComponent } from "./../../Shared/PieChartComponent";
import { SectionCard } from "./../../Shared/SectionCard";
import { colors } from "./../../Shared/theme";
import { getTaskName } from "./utils";
import { getFormattedStatus } from "../../../utils/user";
import PerformanceMetrics from "./../../Shared/PerformanceMetrics";
import TopDriversLeaderboard from "./../../Shared/TopDriversLeaderboard";

interface JobDistribution {
  subCategory: string;
  count: number;
}

interface CompletionTimeData {
  subCategory: string;
  averageDuration: number;
}

interface UserJobMetrics {
  totalJobs: number;
  completedJobs: number;
  inProgressJobs: number;
  pendingJobs: number;
  averageCompletionTime: string;
  onTimeDelivery: string;
  customerSatisfaction: string;
  jobDistribution: JobDistribution[];
  recentActivityData: IUserJob[];
  completionTimeData: CompletionTimeData[];
  locationDistribution: Array<{ location: string; count: number }>;
}

interface PercentageChange {
  totalJobs: string;
  completedJobs: string;
  inProgressJobs: string;
  pendingJobs: string;
  averageCompletionTime: string;
  customerSatisfaction: string;
}

interface UserJobDashboardMetrics {
  currentPeriod: UserJobMetrics;
  previousPeriod: UserJobMetrics;
  percentageChange: PercentageChange;
}

interface DriversStatusCount {
  active: number;
  inactive: number;
  suspended: number;
}

interface DriversSkillsDistribution {
  skill: string;
  count: number;
}

interface PartnerDriver {
  id: string;
  partnerId: string;
  branchId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: { phone: string; country: string } | null;
  dateOfBirth: string;
  license?: {
    licenseNumber: string;
    imageUrl: string;
    dateOfExpiry: string;
    authority: string;
    endorsement: string;
    images: string[];
  } | null;
  status: string;
  profilePicture: string;
  skills: string[];
  createdAt: string;
  updatedAt: string;
}

interface ITopDrivers {
  completedJobs: number;
  averageRating: number;
  driver: PartnerDriver;
}

interface PartnerDriversDashboardMetrics {
  statusCount: DriversStatusCount;
  skillsDistribution: DriversSkillsDistribution[];
  topDrivers: ITopDrivers[];
}

interface CombinedDashboardMetrics {
  userJobMetrics: UserJobDashboardMetrics;
  partnerDriversMetrics?: PartnerDriversDashboardMetrics;
}

interface GetDashboardMetricsVars {
  metricsRangeType: "DAILY" | "WEEKLY" | "MONTHLY";
}

interface GetDashboardMetricsData {
  getPartnerUserJobDashboardMetrics: CombinedDashboardMetrics;
}

// Helper function to parse a trend percentage string (e.g., "12.00%") into a number and boolean flag.
function parseTrend(trendStr: string): { value: number; isPositive: boolean } {
  if (trendStr === "N/A") return { value: 0, isPositive: false };
  const numeric = parseFloat(trendStr.replace("%", ""));
  if (isNaN(numeric)) {
    return { value: 0, isPositive: false };
  }
  return { value: numeric, isPositive: numeric >= 0 };
}

const DashboardPage: React.FC = () => {
  // Manage the metrics range locally.
  const [timePeriod, setTimePeriod] = useState<"daily" | "weekly" | "monthly">(
    "weekly"
  );

  const { data, loading, error, refetch } = useQuery<
    GetDashboardMetricsData,
    GetDashboardMetricsVars
  >(GET_DASHBOARD_METRICS, {
    variables: {
      metricsRangeType: timePeriod.toUpperCase() as
        | "DAILY"
        | "WEEKLY"
        | "MONTHLY"
    },
    fetchPolicy: "network-only"
  });

  const handleTimePeriodChange = (
    event: SelectChangeEvent<"daily" | "weekly" | "monthly">
  ) => {
    const newPeriod = event.target.value as "daily" | "weekly" | "monthly";
    setTimePeriod(newPeriod);
    refetch({
      metricsRangeType: newPeriod.toUpperCase() as
        | "DAILY"
        | "WEEKLY"
        | "MONTHLY"
    });
  };

  const handleRefresh = () => {
    refetch();
  };

  // Loading state with skeletons
  // Loading state with Skeletons that match original style
  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: colors.neutral1,
          color: colors.primary,
          p: 3,
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
          Dashboard Metrics
        </Typography>
        <Skeleton
          variant="text"
          width="60%"
          height={40}
          sx={{ bgcolor: `${colors.neutral2}30` }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={200}
          sx={{ borderRadius: 2, bgcolor: `${colors.neutral2}30` }}
        />
        <Skeleton
          variant="text"
          width="80%"
          sx={{ bgcolor: `${colors.neutral2}30` }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={150}
          sx={{ borderRadius: 2, bgcolor: `${colors.neutral2}30` }}
        />
      </Box>
    );
  }

  // Error state with improved style
  if (error) {
    return (
      <Box
        sx={{
          bgcolor: colors.neutral1,
          color: colors.primary,
          p: 3,
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Typography variant="h6" color="error" sx={{ mb: 1 }}>
          Error loading dashboard metrics.
        </Typography>
        <Typography variant="body1">Please try again later.</Typography>
      </Box>
    );
  }

  const dashboard = data!.getPartnerUserJobDashboardMetrics;
  const userJobMetrics = dashboard.userJobMetrics;
  const partnerDriversMetrics = dashboard.partnerDriversMetrics;

  // Helper function to check if a skill is valid
  function isValidPartnerSkill(skill: PartnerSkills): boolean {
    return Object.values(PartnerSkills).includes(skill);
  }

  // Example: Filtering and mapping skills data for the HorizontalBarChart
  const validSkillsData = partnerDriversMetrics?.skillsDistribution
    .filter((item: any) => {
      const valid = isValidPartnerSkill(item.skill);
      if (!valid) {
        console.warn(`Invalid skill detected: ${item.skill}`);
      }
      return valid;
    })
    .map((item, index) => ({
      skill: item.skill,
      count: item.count,
      color: colors.chartColors[index % colors.chartColors.length]
    }));

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 3,
        bgcolor: colors.neutral1,
        color: colors.primary
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Dashboard Metrics
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={timePeriod}
              onChange={handleTimePeriodChange}
              displayEmpty
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: `${colors.neutral2}50`
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.accent1
                }
              }}
            >
              <MenuItem value="daily">Daily Metrics</MenuItem>
              <MenuItem value="weekly">Weekly Metrics</MenuItem>
              <MenuItem value="monthly">Monthly Metrics</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            sx={{
              borderColor: colors.accent1,
              color: colors.accent1,
              "&:hover": {
                borderColor: colors.accent2,
                backgroundColor: `${colors.accent1}10`
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Job Management KPIs Section */}
      <Grid padding={1}>
        <SectionCard title="Job Management KPIs" icon={<Assignment />}>
          {userJobMetrics.currentPeriod ? (
            <>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard
                    title="Total Jobs"
                    value={userJobMetrics.currentPeriod.totalJobs}
                    icon={<Assignment />}
                    trend={{
                      ...parseTrend(userJobMetrics.percentageChange.totalJobs),
                      period: timePeriod
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard
                    title="Completed Jobs"
                    value={userJobMetrics.currentPeriod.completedJobs}
                    icon={<CheckCircle />}
                    color={colors.success}
                    trend={{
                      ...parseTrend(
                        userJobMetrics.percentageChange.completedJobs
                      ),
                      period: timePeriod
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard
                    title="In Progress Jobs"
                    value={userJobMetrics.currentPeriod.inProgressJobs}
                    icon={<DirectionsCar />}
                    color={colors.accent3}
                    trend={{
                      ...parseTrend(
                        userJobMetrics.percentageChange.inProgressJobs
                      ),
                      period: timePeriod
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard
                    title="Pending Jobs"
                    value={userJobMetrics.currentPeriod.pendingJobs}
                    icon={<AccessTime />}
                    color={colors.warning}
                    trend={{
                      ...parseTrend(userJobMetrics.percentageChange.pendingJobs),
                      period: timePeriod
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                {/* Job Distribution Analysis */}
                <Grid item xs={12} sm={6} md={6}>
                  <SectionCard
                    title="Job Type Distribution"
                    icon={<Assignment />}
                  >
                    {userJobMetrics.currentPeriod.jobDistribution.length > 0 ? (
                      <PieChartComponent
                        data={userJobMetrics.currentPeriod.jobDistribution.map(
                          (item, index) => ({
                            name: getTaskName(item.subCategory),
                            value: item.count,
                            color:
                              colors.chartColors[
                                index % colors.chartColors.length
                              ]
                          })
                        )}
                        innerRadius={40}
                        outerRadius={80}
                      />
                    ) : (
                      <Typography align="center">
                        No Data Currently Available for selected time period,
                        please check again later...
                      </Typography>
                    )}
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <JobLocationDistributionChart
                    locationDistribution={
                      userJobMetrics.currentPeriod.locationDistribution
                    }
                  />
                </Grid>
                {/* Completion Time Analysis */}
                <Grid item xs={12} md={6}>
                  <SectionCard title="Average Completion Time" icon={<Speed />}>
                    {userJobMetrics.currentPeriod.completionTimeData.length >
                    0 ? (
                      <BarChartComponent
                        data={userJobMetrics.currentPeriod.completionTimeData.map(
                          (item) => ({
                            type: getTaskName(item.subCategory),
                            avgTime: item.averageDuration
                          })
                        )}
                      />
                    ) : (
                      <Typography align="center">
                        No Data Currently Available for selected time period,
                        please check again later...
                      </Typography>
                    )}
                  </SectionCard>
                </Grid>
                {/* Performance Metrics Section */}
                <Grid item xs={12} md={6}>
                  <PerformanceMetrics
                    averageCompletionTime={
                      userJobMetrics.currentPeriod.averageCompletionTime
                    }
                    onTimeDelivery={userJobMetrics.currentPeriod.onTimeDelivery}
                    customerSatisfaction={
                      userJobMetrics.currentPeriod.customerSatisfaction
                    }
                  />
                </Grid>
              </Grid>
            </>
          ) : (
            <Typography align="center">
              No Data Currently Available for selected time period, please check
              again later...
            </Typography>
          )}
        </SectionCard>
      </Grid>

      {/* Partner Drivers Metrics Section */}
      <Grid padding={1}>
        <SectionCard title="Partner Drivers Dashboard" icon={<Person />}>
          {partnerDriversMetrics ? (
            <>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={4}>
                  <KpiCard
                    title="Active Drivers"
                    value={partnerDriversMetrics.statusCount.active}
                    icon={<Person />}
                    trend={{ value: 5, isPositive: true, period: timePeriod }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard
                    title="Inactive Drivers"
                    value={partnerDriversMetrics.statusCount.inactive}
                    icon={<AccessTime />}
                    color={colors.warning}
                    trend={{ value: 3, isPositive: false, period: timePeriod }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard
                    title="Suspended Drivers"
                    value={partnerDriversMetrics.statusCount.suspended}
                    icon={<CheckCircle />}
                    color={colors.error}
                    trend={{ value: 2, isPositive: false, period: timePeriod }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                {/* Skills Distribution Analysis */}
                <Grid item xs={12} md={6}>
                  <SectionCard
                    title="Driver Skills Distribution"
                    icon={<Assignment />}
                  >
                    {validSkillsData && validSkillsData?.length > 0 ? (
                      <HorizontalBarChart data={validSkillsData} />
                    ) : (
                      <Typography align="center">
                        No Data Currently Available for selected time period,
                        please check again later...
                      </Typography>
                    )}
                  </SectionCard>
                </Grid>
                {/* Top Drivers Leaderboard */}
                <Grid item xs={12}>
                  <TopDriversLeaderboard
                    topDrivers={partnerDriversMetrics.topDrivers}
                  />
                </Grid>
              </Grid>
            </>
          ) : (
            <Typography align="center">
              No Data Currently Available for selected time period, please check
              again later...
            </Typography>
          )}
        </SectionCard>
      </Grid>

      {/* Recent Activity Section */}
      <Grid padding={1}>
        <SectionCard
          title="Recent Activity"
          icon={<History />}
          action={
            <Button
              size="small"
              sx={{
                color: colors.accent1,
                "&:hover": { bgcolor: `${colors.accent1}10` }
              }}
              onClick={() => {
                window.open(`/user-jobs`, "_blank");
              }}
            >
              View All
            </Button>
          }
        >
          {userJobMetrics.currentPeriod.recentActivityData &&
          userJobMetrics.currentPeriod.recentActivityData.length > 0 ? (
            <Grid container spacing={2}>
              {userJobMetrics.currentPeriod.recentActivityData.map(
                (job: IUserJob) => {
                  const formattedDate = DateTime.fromISO(
                    job.updatedAt
                  ).toLocaleString(DateTime.DATETIME_MED);
                  return (
                    <Grid item key={job.id} xs={12} md={6} lg={4}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${colors.neutral2}20`,
                          height: "100%",
                          transition: "background-color 0.3s ease",
                          "&:hover": { bgcolor: `${colors.neutral1}80` }
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ color: colors.accent1, mb: 1, fontWeight: 600 }}
                        >
                          {getTaskName(job.subCategory)}
                        </Typography>
                        {job?.booking?.referenceNumber && (
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.primary,
                              mb: 1,
                              fontStyle: "italic"
                            }}
                          >
                            Booking Reference: {job.booking.referenceNumber}
                          </Typography>
                        )}
                        <Typography
                          variant="body1"
                          sx={{ color: colors.primary, mb: 1 }}
                        >
                          Status:{" "}
                          <strong>{getFormattedStatus(job.status)}</strong>
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: colors.neutral3 }}
                        >
                          Updated at: {formattedDate}{" "}
                          {job.updatedBy?.firstName &&
                            `by ${job.updatedBy.firstName} ${job.updatedBy.lastName}`}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                }
              )}
            </Grid>
          ) : (
            <Typography align="center">
              No Data Currently Available for selected time period, please check
              again later...
            </Typography>
          )}
        </SectionCard>
      </Grid>
    </Box>
  );
};

export default DashboardPage;